import React, { useState } from "react";
import { fmtShort } from "./AvailabilityPill";

// Paket Injuries Teil 3 (Marin 16.09.2026, Brief SP-INJURIES §Teil 3): the injury history under the transfer
// history in the Transfers tab — same content as the ScoutPitch career block: header + summary
// "{days} days · {n} absences · last 3 seasons", one collapsible group per season (first open), one row per
// entry: type dot (injured/questionable amber, suspended red), reason (fallback "Injury"), period
// (open = "ongoing"; the start shows its year when it differs from the end year or the entry is open and
// started in an earlier year), "14 d", "5 matches missed" (0 = omitted), club crest. `injuries: null` → nothing.
//
// Payload (GET /dashboard/players/{id}/injuries/ → injuries):
//   { seasons: [{ season_label, items: [{ type, reason, from, to, open, days, missed_matches, club{id,name,crest} }] }],
//     summary: { days_last_3_seasons, items_last_3_seasons, basis } }

const TONE = { injured: "#B45309", questionable: "#B45309", suspended: "#DC2626" };
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

export const spanText = (it) => {
  const end = it.open || !it.to ? null : it.to;
  const endYear = end ? end.slice(0, 4) : String(new Date().getUTCFullYear());
  return `${fmtShort(it.from, (it.from || "").slice(0, 4) !== endYear)} – ${end ? fmtShort(end, true) : "ongoing"}`;
};

const InjuryHistoryBlock = ({ injuries }) => {
  const [open, setOpen] = useState([0]);
  const seasons = (injuries?.seasons || []).filter((s) => (s.items || []).length);
  if (!seasons.length) return null;
  const s = injuries.summary;
  const parts = [
    s?.days_last_3_seasons != null ? plural(s.days_last_3_seasons, "day", "days") : "",
    s?.items_last_3_seasons != null ? plural(s.items_last_3_seasons, "absence", "absences") : "",
  ].filter(Boolean);
  const summary = parts.length ? `${parts.join(" · ")} · last 3 seasons` : "";
  const toggle = (i) => setOpen((o) => (o.includes(i) ? o.filter((x) => x !== i) : [...o, i]));
  return (
    <div className="bg-white rounded-[20px] border border-[#D6DBEA] overflow-hidden mt-4" data-testid="injury-history">
      <div className="bg-[#F0F0F2] border-b border-[rgba(214,219,234,0.3)] px-4 py-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[#1A212F] text-xs sm:text-sm lg:text-base font-bold leading-[19.5px] uppercase tracking-wide">Injuries</div>
        {summary && <div className="text-xs sm:text-sm text-gray-500 font-medium uppercase tracking-wide">{summary}</div>}
      </div>
      {seasons.map((sea, i) => {
        const isOpen = open.includes(i);
        const days = sea.items.reduce((a, it) => a + (it.days || 0), 0);
        const head = [plural(sea.items.length, "absence", "absences"), days ? `${days} d` : ""].filter(Boolean).join(" · ");
        return (
          <div key={sea.season_label || i} data-testid="injury-season" data-open={isOpen ? "1" : "0"}>
            <button
              type="button"
              className="w-full min-h-[44px] flex items-center gap-3 px-4 py-2 text-left border-b border-[rgba(214,219,234,0.5)] hover:bg-gray-50"
              aria-expanded={isOpen}
              onClick={() => toggle(i)}
            >
              <span className="text-sm font-semibold text-[#1A212F]">{sea.season_label}</span>
              <span className="text-xs text-gray-500 uppercase tracking-wide">{head}</span>
              <span className="ml-auto text-gray-400" aria-hidden="true" style={{ transform: isOpen ? "rotate(180deg)" : "none" }}>▾</span>
            </button>
            {isOpen &&
              sea.items.map((it, j) => (
                <div
                  key={`${it.from || ""}-${j}`}
                  className="flex items-center flex-wrap gap-x-3 gap-y-1 px-4 py-2.5 border-b border-[rgba(214,219,234,0.5)]"
                  style={{ borderLeft: `3px solid ${TONE[it.type] || TONE.injured}` }}
                  data-testid="injury-row"
                >
                  <span className="inline-block w-2 h-2 rounded-full flex-shrink-0" style={{ background: TONE[it.type] || TONE.injured }} aria-hidden="true" />
                  <span className="text-sm font-semibold text-[#1A212F]">{it.reason || "Injury"}</span>
                  <span className="text-xs text-gray-500 whitespace-nowrap">{spanText(it)}</span>
                  <span className="basis-full sm:basis-auto sm:ml-auto flex items-center gap-3 pl-5 sm:pl-0 text-xs text-gray-500 whitespace-nowrap">
                    {it.days != null && <span>{it.days} d</span>}
                    {it.missed_matches > 0 && <span>{plural(it.missed_matches, "match missed", "matches missed")}</span>}
                    {it.club?.crest ? (
                      <img src={it.club.crest} alt="" title={it.club.name || ""} className="w-6 h-6 object-contain rounded-full flex-shrink-0 bg-white border border-[#D6DBEA]" />
                    ) : it.club?.name ? (
                      <span className="text-xs text-gray-400">{it.club.name}</span>
                    ) : null}
                  </span>
                </div>
              ))}
          </div>
        );
      })}
    </div>
  );
};

export default InjuryHistoryBlock;
